import { useCurrentCompany } from "api/company.api";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Uppdrag, UppdragStatus } from "types/uppdrag.type";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { patchUppdrag, UppdragQueryKey } from "api/uppdrag.api";
import { useNotification } from "hooks/useNotification";

const reportedHoursSchema = z
  .object({
    reportedWorkingHours: z.string().optional(),
    reportedDrivingHours: z.string().optional(),
  })
  .refine(
    (data) => !!data.reportedWorkingHours || !!data.reportedDrivingHours,
    {
      message: "Ange arbetad tid eller körtid",
      path: ["reportedWorkingHours"],
    },
  );

type TReportedHoursSchema = z.infer<typeof reportedHoursSchema>;

export const useTimeReportLogic = (uppdrag?: Uppdrag) => {
  const prevStatusRef = useRef(uppdrag?.status);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { data: company } = useCurrentCompany();

  const form = useForm<TReportedHoursSchema>({
    resolver: zodResolver(reportedHoursSchema),
  });

  // Öppna tidsrapporteringsmodalen när uppdragsstatus skiftar till Completed
  useEffect(() => {
    if (
      prevStatusRef.current === UppdragStatus.Started &&
      uppdrag?.status === UppdragStatus.Completed &&
      company?.isInvoicingEnabled && // Alla företag har inte aktiverat detta
      !uppdrag.timeReportData?.reportedWorkingHours &&
      !uppdrag.timeReportData?.reportedDrivingHours
    ) {
      setIsModalOpen(true);
    }

    // Update the ref with current status for next render
    prevStatusRef.current = uppdrag?.status;
  }, [uppdrag, company]);

  const n = useNotification();
  const qc = useQueryClient();

  const patchMutation = useMutation({
    ...patchUppdrag(),
    onSuccess: () => {
      setIsModalOpen(false);
      n.info("Tidsrapport registrerad.");
      qc.invalidateQueries({
        queryKey: [UppdragQueryKey.GetList],
      });
      qc.invalidateQueries({
        queryKey: [UppdragQueryKey.GetOne],
      });
    },
  });

  const onSubmit = form.handleSubmit((payload: TReportedHoursSchema) => {
    if (!uppdrag) {
      n.error("Något gick fel, kontakta support.");
      return;
    }
    patchMutation.mutate({
      id: uppdrag.id,
      payload: {
        reportedWorkingHours: payload.reportedWorkingHours || undefined,
        reportedDrivingHours: payload.reportedDrivingHours || undefined,
      },
    });
  });

  return {
    isModalOpen,
    form,
    closeModal: () => setIsModalOpen(false),
    afterClose: () => form.reset(),
    onSubmit,
    isPending: patchMutation.isPending,
  };
};
